import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { CheckCircle, AlertTriangle, Package, Lightbulb, Loader2, ClipboardList, Sparkles, Wallet as WalletIcon, PlusCircle, Lock } from 'lucide-react';

export default function MyPackage() {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [purchasingId, setPurchasingId] = useState(null); // plan._id currently being purchased, for per-card loading state
  const [settings, setSettings] = useState({ dailyRoiPercent: 1 });

  useEffect(() => {
    api.get('/packages/my').then(r => setPackages(r.data)).catch(console.error).finally(() => setLoading(false));
    api.get('/settings/roi').then(r => setSettings(r.data)).catch(console.error);
    api.get('/plans').then(r => setPlans(r.data)).catch(console.error).finally(() => setPlansLoading(false));
  }, []);

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Purchasing is instant now — money was already verified when an admin
  // approved the deposit request, so there's nothing left to fill in here.
  // Just spend the (already-verified) wallet balance on one of admin's plans.
  const handlePurchase = async (plan) => {
    if (!window.confirm(`Activate the "${plan.name}" package for ${formatCurrency(plan.amount)}? This will be deducted from your wallet.`)) {
      return;
    }
    setPurchasingId(plan._id);
    try {
      const res = await api.post('/packages/activate', { planId: plan._id });
      toast.success(res.data.message);
      setPackages(prev => [res.data.package, ...prev]);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate package');
    } finally {
      setPurchasingId(null);
    }
  };

  const activePackage = packages.find(p => p.status === 'active');
  const depositWallet = user?.depositWallet || 0;

  return (
    <DashboardLayout>
      <h1 className="page-title">My Package</h1>
      <p className="page-subtitle">Manage your investment package</p>

      {/* Active Package Status */}
      {user?.isActive && activePackage ? (
        <div className="card" style={{ marginBottom: 24, background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(var(--primary-rgb),0.08))', borderColor: 'rgba(34,197,94,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ color: '#22c55e', display: 'flex', alignItems: 'center' }}><CheckCircle size={48} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#22c55e', marginBottom: 4 }}>Package Active</div>
              <div style={{ color: 'var(--text-light)' }}>Amount: <strong style={{ color: 'var(--primary)', fontSize: '1.3rem' }}>₹{activePackage.amount?.toLocaleString('en-IN')}</strong></div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
                Activated: {new Date(activePackage.activatedAt).toLocaleDateString('en-IN')} &nbsp;|&nbsp;
                Expires: {new Date(activePackage.expiresAt).toLocaleDateString('en-IN')}
              </div>
            </div>
            <div style={{ textAlign: 'center', background: 'rgba(var(--primary-rgb),0.1)', padding: '16px 24px', borderRadius: 12 }}>
              <div style={{ fontFamily: 'Rajdhani', fontSize: '2rem', fontWeight: 700, color: 'var(--primary)' }}>{settings.dailyRoiPercent}%</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Daily ROI</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-warning" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} /> You don't have an active package yet. Add funds to your wallet, then pick a package below.
        </div>
      )}

      {/* Balance lives on the Wallet page (single source of truth) — just a
          quick link + inline figure here, not a duplicate wallet box. */}
      <div className="alert alert-info" style={{ marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <WalletIcon size={16} /> Deposit Wallet balance: <strong style={{ color: 'var(--primary)' }}>{formatCurrency(depositWallet)}</strong>
        </span>
        <Link to="/dashboard/deposit" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <PlusCircle size={14} /> Add Funds
        </Link>
      </div>

      {/* Available Packages (admin-defined plans) — the ONLY thing a user
          can buy. There is no custom-amount option; every plan and its
          price is fixed by admin. */}
      <div className="card" style={{ marginBottom: 28 }}>
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={18} /> Available Packages</h3>
        </div>
        {plansLoading ? (
          <div className="loader-wrap"><div className="loader"></div></div>
        ) : plans.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📦</div><h3>No packages available yet</h3><p style={{ color: 'var(--text-muted)' }}>Admin hasn't published any packages yet — check back soon.</p></div>
        ) : (
          <div className="grid-3" style={{ gap: 18 }}>
            {plans.map(plan => {
              const canAfford = depositWallet >= plan.amount;
              const disabled = user?.isActive || !canAfford || purchasingId === plan._id;
              return (
                <div
                  key={plan._id}
                  className="income-type-card"
                  style={{ flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--primary)', marginBottom: 4 }}>{plan.name}</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 6 }}>₹{plan.amount.toLocaleString('en-IN')}</div>
                  {plan.description && <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 10 }}>{plan.description}</div>}
                  {plan.features && plan.features.length > 0 && (
                    <ul style={{ listStyle: 'none', marginBottom: 14, flex: 1 }}>
                      {plan.features.map((f, i) => (
                        <li key={i} style={{ display: 'flex', gap: 8, color: 'var(--text-light)', fontSize: '0.82rem', marginBottom: 6 }}>
                          <CheckCircle size={14} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} /> {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>Valid for {plan.validityDays} days</div>

                  {!user?.isActive && !canAfford && (
                    <div style={{ fontSize: '0.76rem', color: '#f59e0b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Lock size={12} /> Need {formatCurrency(plan.amount - depositWallet)} more —{' '}
                      <Link to="/dashboard/deposit" style={{ color: 'var(--primary)', fontWeight: 700 }}>Add Funds</Link>
                    </div>
                  )}

                  <button
                    type="button"
                    className={`btn ${canAfford && !user?.isActive ? 'btn-primary' : 'btn-outline'} btn-sm btn-block`}
                    onClick={() => handlePurchase(plan)}
                    disabled={disabled}
                  >
                    {purchasingId === plan._id ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Loader2 size={14} className="spin" /> Activating...</span>
                    ) : user?.isActive ? 'Already Active' : canAfford ? 'Purchase Now' : 'Insufficient Balance'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid-2" style={{ gap: 24, marginBottom: 28 }}>
        {/* Package Info */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><ClipboardList size={18} /> Package Rules</h3></div>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { icon: '▸', text: 'You can only purchase packages defined by admin — no custom amount.' },
              { icon: '▸', text: 'Purchases are paid from your wallet balance only.' },
              { icon: '▸', text: 'Add funds first (admin approval required) if your balance is short.' },
              { icon: '▸', text: 'Package valid for the number of days shown on the plan.' },
              { icon: '▸', text: `${settings.dailyRoiPercent}% daily ROI (as set by admin)` },
              { icon: '▸', text: 'Capping ₹1,00,000 per day' },
            ].map(r => (
              <li key={r.text} style={{ display: 'flex', gap: 10, color: 'var(--text-light)', fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--primary)' }}>{r.icon}</span> {r.text}
              </li>
            ))}
          </ul>
        </div>

        <div className="alert alert-info" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, alignSelf: 'flex-start', margin: 0 }}>
          <Lightbulb size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            <strong>How it works:</strong> Request funds on the{' '}
            <Link to="/dashboard/deposit" style={{ color: 'var(--primary)', fontWeight: 700 }}>Add Funds</Link> page →
            admin approves it into your wallet → come back here and purchase any package your balance can cover, instantly.
          </span>
        </div>
      </div>

      {/* Package History */}
      <div className="card">
        <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Package size={16} /> Package History</h3></div>
        {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
          packages.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">📦</div><h3>No packages yet</h3></div>
          ) : (
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr><th>Date</th><th>Amount</th><th>Activated</th><th>Expires</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {packages.map(p => (
                    <tr key={p._id}>
                      <td style={{ fontSize: '0.85rem' }}>{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                      <td style={{ color: 'var(--primary)', fontWeight: 700 }}>₹{p.amount?.toLocaleString('en-IN')}</td>
                      <td style={{ fontSize: '0.85rem' }}>{p.activatedAt ? new Date(p.activatedAt).toLocaleDateString('en-IN') : '—'}</td>
                      <td style={{ fontSize: '0.85rem' }}>{p.expiresAt ? new Date(p.expiresAt).toLocaleDateString('en-IN') : '—'}</td>
                      <td>
                        <span className={`badge badge-${p.status === 'active' ? 'success' : p.status === 'pending' ? 'warning' : p.status === 'rejected' ? 'danger' : 'info'}`}>
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </DashboardLayout>
  );
}